// ConceptView.js : Composant principal qui orchestre toute la vue des concepts

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { X } from 'lucide-react';

// Importer les composants des autres modules
import { ConceptFilters, NoResultsFound } from './ConceptFilter';
import { ConceptCard, ConceptListItem, ConceptsByCategory, LoadingIndicator } from './ConceptDisplay';
import { ConceptDetailDialog } from './ConceptDetail';
import { 
  fetchConceptsData,
  filterConcepts,
  sortConcepts,
  groupConceptsByCategory,
  extractAvailableCategories
} from './ConceptServices';

// Composant principal pour l'exploration des concepts
const ConceptView = ({ 
  userId, 
  competence, 
  selectedConcept = null, 
  onClose, 
  makeAuthenticatedRequest, 
  onError,
  onSelectConcept // Nouvelle prop pour permettre la sélection externe
}) => {
  // États pour les données
  const [concepts, setConcepts] = useState([]);
  const [conceptDescriptions, setConceptDescriptions] = useState([]);
  const [selectedConceptData, setSelectedConceptData] = useState(null);
  const [loading, setLoading] = useState(false);
  // Nouvel état pour suivre le chargement d'un concept spécifique
  const [loadingSelectedConcept, setLoadingSelectedConcept] = useState(!!selectedConcept);
  
  // États pour les filtres
  const [searchQuery, setSearchQuery] = useState('');
  const [groupBy, setGroupBy] = useState('relevance'); // 'relevance', 'alphabetical', 'count', 'category'
  const [viewMode, setViewMode] = useState('grid'); // 'grid', 'list'
  const [hasContextFilter, setHasContextFilter] = useState(null); // null = all, true = with context, false = without context
  const [categoriesFilter, setCategoriesFilter] = useState([]);
  
  // Récupérer les concepts depuis l'API
  const fetchConcepts = useCallback(async () => {
    if (!userId || !competence?.id) return;
    
    try {
      setLoading(true);
      
      const result = await fetchConceptsData(userId, competence.id, makeAuthenticatedRequest);
      
      if (result.error) {
        throw result.error;
      }
      
      setConcepts(result.concepts);
      setConceptDescriptions(result.descriptions);
      
    } catch (err) {
      if (onError) {
        onError(err, 'Récupération des concepts');
      } else {
        console.error('Erreur lors de la récupération des concepts:', err);
      }
    } finally {
      setLoading(false);
    }
  }, [userId, competence, makeAuthenticatedRequest, onError]);

  // Charger les concepts au montage
  useEffect(() => {
    fetchConcepts();
  }, [fetchConcepts]);
  
  // Gérer le cas d'un concept spécifique sélectionné
  useEffect(() => {
    if (selectedConcept && concepts.length > 0) {
      setLoadingSelectedConcept(true);
      
      console.log("Recherche du concept sélectionné:", selectedConcept);
      
      // Déterminer le nom du concept à rechercher
      const conceptName = typeof selectedConcept === 'string' 
        ? selectedConcept 
        : (selectedConcept.concept || selectedConcept.title);
      
      console.log("Nom du concept à rechercher:", conceptName);
      
      // Rechercher le concept dans la liste des concepts chargés
      const foundConcept = concepts.find(c => 
        (c.concept && (c.concept === conceptName)) || 
        (c.title && (c.title === conceptName))
      );
      
      if (foundConcept) {
        console.log("Concept trouvé:", foundConcept);
        
        // Rechercher la description du concept
        const conceptDesc = conceptDescriptions.find(d => 
          (d.concept && (d.concept === conceptName)) || 
          (d.title && (d.title === conceptName))
        );
        
        // Ouvrir automatiquement le dialogue des détails
        setSelectedConceptData({
          ...foundConcept,
          description: conceptDesc
        });
      } else {
        console.log("Concept non trouvé dans la liste des concepts disponibles");
      }
      
      // Dans tous les cas, désactiver l'état de chargement
      setLoadingSelectedConcept(false);
    }
  }, [selectedConcept, concepts, conceptDescriptions]);

  // Extraire les catégories disponibles
  const availableCategories = useMemo(() => {
    return extractAvailableCategories(concepts);
  }, [concepts]);

  // Filtrer les concepts selon les critères actuels
  const filteredConcepts = useMemo(() => {
    return filterConcepts(concepts, searchQuery, hasContextFilter, categoriesFilter, conceptDescriptions);
  }, [concepts, searchQuery, hasContextFilter, categoriesFilter, conceptDescriptions]);
  
  // Trier les concepts selon le critère sélectionné
  const sortedConcepts = useMemo(() => {
    return sortConcepts(filteredConcepts, groupBy);
  }, [filteredConcepts, groupBy]);
  
  // Regrouper les concepts par catégorie si nécessaire
  const conceptsByCategory = useMemo(() => {
    if (groupBy !== 'category') return null;
    return groupConceptsByCategory(sortedConcepts);
  }, [sortedConcepts, groupBy]);
  
  // Gestionnaire amélioré pour la sélection de concept
  const handleConceptSelect = useCallback((concept) => {
    // Mettre à jour l'état local
    setSelectedConceptData(concept);
    
    // Si un gestionnaire externe est fourni, l'appeler également
    if (onSelectConcept) {
      onSelectConcept(concept);
    }
  }, [onSelectConcept]);
  
  // Navigation vers un concept lié
  const handleSelectRelatedConcept = useCallback((conceptTitle) => {
    // Chercher dans les deux propriétés concept et title
    const concept = concepts.find(c => 
      c.concept === conceptTitle || c.title === conceptTitle
    );
    
    if (concept) {
      // Chercher dans les deux propriétés concept et title
      const description = conceptDescriptions.find(desc => 
        desc.concept === conceptTitle || desc.title === conceptTitle
      );
      
      const conceptData = {
        title: conceptTitle,
        concept: conceptTitle,  // Assurer la présence des deux formats
        documents: concept.documents,
        description,
        category: concept.category
      };
      
      // Mettre à jour l'état local
      setSelectedConceptData(conceptData);
      
      // Si un gestionnaire externe est fourni, l'appeler également
      if (onSelectConcept) {
        onSelectConcept(conceptData);
      }
    }
  }, [concepts, conceptDescriptions, onSelectConcept]);

  // Réinitialiser tous les filtres
  const resetFilters = useCallback(() => {
    setSearchQuery('');
    setHasContextFilter(null);
    setCategoriesFilter([]);
  }, []);

  // Vérifier si des filtres sont actifs
  const hasActiveFilters = searchQuery || hasContextFilter !== null || categoriesFilter.length > 0;

  // Rendu du composant
  return (
    <div className={`bg-gray-50 rounded-lg ${onClose ? 'p-0' : 'p-6'}`}>
      {/* Condition de rendu avec trois cas possibles */}
      {loadingSelectedConcept ? (
        // 1. En cours de chargement d'un concept spécifique
        <div className="flex justify-center items-center p-6">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div>
          <span className="ml-3 text-gray-600">Chargement du concept...</span>
        </div>
      ) : selectedConceptData ? (
        // 2. Un concept spécifique est sélectionné et chargé
        <ConceptDetailDialog
          concept={selectedConceptData}
          isOpen={true}
          onClose={() => {
            setSelectedConceptData(null);
            // Si c'était un concept sélectionné depuis l'extérieur, fermer complètement la vue
            if (selectedConcept && onClose) {
              onClose();
            }
          }}
          onSelectRelatedConcept={handleSelectRelatedConcept}
          userId={userId}
          competence={competence}
          makeAuthenticatedRequest={makeAuthenticatedRequest}
          onError={onError}
          onSelectConcept={onSelectConcept} // Ajouter la propriété pour permettre la sélection
        />
      ) : (
        // 3. Mode normal : affichage de la liste des concepts
        <>
          {/* En-tête avec le titre et le bouton de fermeture */}
          {onClose && (
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center bg-white rounded-t-lg">
              <h2 className="text-xl font-semibold text-gray-900">Exploration par concept</h2>
              <button
                onClick={onClose}
                className="p-2 text-gray-500 hover:bg-gray-100 rounded-full transition-colors"
                aria-label="Fermer"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          )}
          
          <div className={`${onClose ? 'p-6' : ''} space-y-2`}>
            {/* Barre de recherche et options de filtrage */}
            <ConceptFilters
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              groupBy={groupBy}
              setGroupBy={setGroupBy}
              viewMode={viewMode}
              setViewMode={setViewMode}
              hasContextFilter={hasContextFilter}
              setHasContextFilter={setHasContextFilter}
              categoriesFilter={categoriesFilter}
              setCategoriesFilter={setCategoriesFilter}
              availableCategories={availableCategories}
            />

            {/* État de chargement */}
            {loading ? (
              <LoadingIndicator />
            ) : filteredConcepts.length === 0 ? (
              <NoResultsFound 
                hasFilters={hasActiveFilters} 
                resetFilters={resetFilters} 
              />
            ) : (
              <>
                
                {/* Affichage des concepts regroupés par catégorie ou en liste/grille */}
                {groupBy === 'category' ? (
                  <ConceptsByCategory 
                    categories={conceptsByCategory} 
                    viewMode={viewMode}
                    onSelectConcept={handleConceptSelect} // Utiliser handleConceptSelect 
                  />
                ) : (
                  viewMode === 'grid' ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {sortedConcepts.map((concept) => (
                        <ConceptCard
                          key={concept.title}
                          title={concept.title}
                          count={concept.count}
                          documents={concept.documents}
                          conceptDescriptions={conceptDescriptions}
                          category={concept.category}
                          onSelect={handleConceptSelect} // Utiliser handleConceptSelect
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {sortedConcepts.map((concept) => (
                        <ConceptListItem
                          key={concept.title}
                          title={concept.title}
                          count={concept.count}
                          documents={concept.documents}
                          conceptDescriptions={conceptDescriptions}
                          category={concept.category}
                          onSelect={handleConceptSelect} // Utiliser handleConceptSelect
                        />
                      ))}
                    </div>
                  )
                )}
              </>
            )}
            
            {/* Dialog pour afficher les détails d'un concept (quand sélectionné depuis la liste) */}
            <ConceptDetailDialog
              concept={selectedConceptData}
              isOpen={!!selectedConceptData}
              onClose={() => setSelectedConceptData(null)}
              onSelectRelatedConcept={handleSelectRelatedConcept}
              userId={userId}
              competence={competence}
              makeAuthenticatedRequest={makeAuthenticatedRequest}
              onError={onError}
              onSelectConcept={onSelectConcept} // Ajouter la propriété pour permettre la sélection
            />
          </div>
        </>
      )}
    </div>
  );
};

export default ConceptView;
